import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { createContext, useContext, useCallback, useState, useMemo, useEffect } from "react";
import { omit, equals, pipe } from "ramda";
import { useNavigate, useSearchParams } from "react-router-dom";
import querystring from "query-string";
import { filterDefaultParams, repairQueryValues } from "utils/queryParams";
import { useCustomEffect } from "hooks";
import { isoDateConvertKeys } from "utils/dates";
import { parseSearchParams, readValues, transformSearch } from "./searchParams.utils";
import { isNotEmptyObject } from "utils/validation";
import { jsx as _jsx } from "react/jsx-runtime";
export var QueryContext = /*#__PURE__*/createContext({});
export var QueryContextProvider = function QueryContextProvider(props) {
  var _props$paramTypes = props.paramTypes,
    paramTypes = _props$paramTypes === void 0 ? {} : _props$paramTypes,
    _props$defaultParams = props.defaultParams,
    defaultParams = _props$defaultParams === void 0 ? {} : _props$defaultParams,
    _props$omittedState = props.omittedState,
    omittedState = _props$omittedState === void 0 ? [] : _props$omittedState,
    _props$alwaysInclude = props.alwaysInclude,
    alwaysInclude = _props$alwaysInclude === void 0 ? [] : _props$alwaysInclude,
    _props$updateAppState = props.updateAppStateOnSearchChanges,
    updateAppStateOnSearchChanges = _props$updateAppState === void 0 ? false : _props$updateAppState;
  var _useSearchParams = useSearchParams(),
    _useSearchParams2 = _slicedToArray(_useSearchParams, 1),
    searchParams = _useSearchParams2[0];
  var navigate = useNavigate();
  var searchParamsObject = useCallback(function (x) {
    var parsedSearchParams = parseSearchParams(x);
    return isNotEmptyObject(parsedSearchParams) ? pipe(readValues(defaultParams), transformSearch(defaultParams, paramTypes), repairQueryValues)(parsedSearchParams) : {};
  }, [defaultParams, paramTypes]);
  var _useState = useState(_objectSpread(_objectSpread({}, defaultParams), searchParamsObject(searchParams))),
    _useState2 = _slicedToArray(_useState, 2),
    appState = _useState2[0],
    setAppState = _useState2[1];
  var setPartialAppState = useCallback(function (state) {
    setAppState(function (prev) {
      return _objectSpread(_objectSpread({}, prev), state);
    });
  }, [setAppState]);
  useCustomEffect(function () {
    var updatedParams = omit(omittedState, appState);
    var filteredParams = filterDefaultParams(defaultParams, updatedParams, alwaysInclude);
    var dateConverted = isoDateConvertKeys(["dateFrom", "dateTo"], filteredParams);
    var stringifiedSearch = "?".concat(querystring.stringify(dateConverted, {
      encode: false
    }).toLowerCase());
    if (!equals(location.search, stringifiedSearch)) {
      navigate("".concat(location.pathname).concat(stringifiedSearch), {
        state: appState,
        replace: true
      });
    }
  }, [appState]);
  useEffect(function () {
    var onPopstate = function onPopstate(_ref) {
      var state = _ref.state;
      setPartialAppState(state.usr);
    };
    window.addEventListener("popstate", onPopstate);
    return function () {
      var _window;
      return (_window = window) === null || _window === void 0 ? void 0 : _window.removeEventListener("popstate", onPopstate);
    };
  }, [setPartialAppState]);
  var currSearchParams = searchParamsObject(searchParams);
  useCustomEffect(function () {
    if (updateAppStateOnSearchChanges) {
      setPartialAppState(searchParamsObject(searchParams));
    }
  }, [searchParamsObject(searchParams)]);
  return /*#__PURE__*/_jsx(QueryContext.Provider, _objectSpread({
    value: useMemo(function () {
      return {
        appState: appState,
        setPartialAppState: setPartialAppState,
        setAppState: setAppState,
        currSearchParams: currSearchParams
      };
    }, [appState, setPartialAppState, currSearchParams])
  }, props));
};
export var useQueryContext = function useQueryContext() {
  return useContext(QueryContext);
};